'use client'

import Link from 'next/link'
import Image from 'next/image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faHeadset, faPhone, faLocationDot, faBuilding, faUserTie, faClock, faIdCard } from '@fortawesome/free-solid-svg-icons'
import { 
  faFacebookF, 
  faTwitter, 
  faYoutube, 
  faTelegram,
  faPinterest,
  faLinkedinIn,
  faTwitch,
  faSoundcloud,
  faVimeo,
  faQuora,
  faReddit,
  fa500px,
  faTiktok
} from '@fortawesome/free-brands-svg-icons'
import { BRAND } from '@/config/domain.config'

interface Page {
  id: string
  title: string
  slug: string
  uri: string
}

interface ClientFooterProps {
  pages: Page[]
}

const links = [
  { name: 'Bảng xếp hạng', href: '/bang-xep-hang-bong-da' },
  { name: 'Lịch thi đấu', href: '/lich-thi-dau-bong-da' },
  { name: 'Kết quả bóng đá', href: '/kqbd' },
  { name: 'Highlights', href: '/highlights' },
  { name: 'Tin tức', href: '/tin-bong-da' },
  { name: 'Nhận định', href: '/nhan-dinh-bong-da' },
]

const contacts = [
  { 
    icon: faBuilding, 
    text: (
      <span className="address-container">
        Thương hiệu: <Link href="/" className="text-[var(--primary)] hover:text-white transition-colors whitespace-nowrap">
          Okwin TV
        </Link>
      </span>
    )
  },
  { 
    icon: faUserTie, 
    text: (
      <>
        Người đại diện: Huỳnh Tấn Tài
      </>
    )
  },
  { 
    icon: faLocationDot, 
    text: (
      <span className="address-container">
          Địa chỉ: 730/7/22 đường Huỳnh Tấn Phát, Phường Tân Mỹ, Thành phố Hồ Chí Minh, Việt Nam
       
      </span>
    )
  },
  { icon: faPhone, text: 'Phone: +84318893644' },
  { icon: faEnvelope, text: 'Email: contact@okwintv.top' },
  { icon: faClock, text: 'Thời gian hoạt động: 24/7' },
]

const socialLinks = [
  // { icon: faFacebookF, href: 'https://www.facebook.com/cakhiatvorguk/', name: 'Facebook' },
  // { icon: faTwitter, href: 'https://x.com/cakhiatvorguk', name: 'Twitter' },
  // { icon: faPinterest, href: 'https://www.pinterest.com/cakhiatvorguk/', name: 'Pinterest' },
  // { icon: faLinkedinIn, href: 'https://www.linkedin.com/in/cakhiatvorguk/', name: 'LinkedIn' },
  { icon: faYoutube, href: 'https://www.youtube.com/@okwintvtructiepbongda', name: 'YouTube' }
  // { icon: faTwitch, href: 'https://www.twitch.tv/cakhiatvorguk', name: 'Twitch' },
  // { icon: faSoundcloud, href: 'https://soundcloud.com/cakhiatvorguk', name: 'SoundCloud' },
  // { icon: faVimeo, href: 'https://vimeo.com/cakhiatvorguk', name: 'Vimeo' },
  // { icon: faQuora, href: 'https://www.quora.com/profile/CAKHIA-TV-TV-3', name: 'Quora' }
]

const relatedPlatforms = [
  { name: 'Xoilac', href: 'https://xoilactv.tech' },
  { name: 'Socolive', href: 'https://socolivees.tv/' },
  { name: 'CakhiaTV', href: 'https://cakhiatv.org.u/' },
  { name: '90phut', href: 'https://90phut0.com' },
  { name: 'VeboTV', href: 'https://vebotv9.com/' },
  { name: 'GavangTV', href: 'https://gavantv.tv' }
]

export default function ClientFooter({ pages }: ClientFooterProps) {
  return (
    <footer className="bg-[#2a2a2a] pt-15 pb-8 mt-15 border-t border-white/10">
      <div className="container mx-auto px-4 pt-4">
        <div className="grid gap-10 mb-10 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <div className="mb-5">
              <Link href="/">
                <Image
                  src="/images/logo.png"
                  alt={BRAND.NAME}
                  width={200}
                  height={66}
                  className="w-[180px] h-auto"
                  quality={100}
                  suppressHydrationWarning
                />
              </Link>
            </div>
            <div itemScope itemType="https://schema.org/SportsOrganization">
            <p className="text-gray-400 text-sm mb-4">
              <Link href="/" className="text-[var(--primary)] hover:text-white transition-colors">
               Okwin TV
              </Link> là nền tảng xem trực tiếp bóng đá miễn phí tại Việt Nam, mang đến chất lượng hình ảnh sắc nét, tốc độ ổn định, cập nhật nhanh lịch thi đấu và link xem của các giải đấu lớn như Ngoại Hạng Anh, Cúp C1, La Liga, Serie A, Bundesliga và V-League.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Theo dõi Cakhia TV trên ${link.name}`}
                  title={`Theo dõi Cakhia TV trên ${link.name}`}
                  className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[var(--primary)] hover:transform hover:-translate-y-0.5 transition-all"
                >
                  <FontAwesomeIcon icon={link.icon} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-white text-base font-semibold uppercase mb-5 pb-2.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[50px] after:h-0.5 after:bg-[var(--primary)]">
              Thông tin chung
            </h3>
            <ul className="space-y-3">
              {pages.map((page: Page) => (
                <li key={page.id}>
                  <Link
                    href={`/${page.slug}`}
                    className="text-gray-400 text-sm hover:text-[var(--primary)] hover:translate-x-1.5 transition-all flex items-center before:content-['\203A'] before:mr-2 before:text-[var(--primary)] before:text-lg before:leading-none"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white text-base font-semibold uppercase mb-5 pb-2.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[50px] after:h-0.5 after:bg-[var(--primary)]">
              Liên kết
            </h3>
            <ul className="space-y-3">
              {links.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-gray-400 text-sm hover:text-[var(--primary)] hover:translate-x-1.5 transition-all flex items-center before:content-['\203A'] before:mr-2 before:text-[var(--primary)] before:text-lg before:leading-none"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white text-base font-semibold uppercase mb-5 pb-2.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[50px] after:h-0.5 after:bg-[var(--primary)]">
              Liên hệ
            </h3>
            <ul className="space-y-4">
              {contacts.map((contact, index) => (
                <li key={index} className="flex items-center gap-2.5 text-gray-400 text-sm">
                  <span className="w-[30px] h-[30px] bg-white/10 rounded-full flex items-center justify-center text-[var(--primary)] flex-shrink-0">
                    <FontAwesomeIcon icon={contact.icon} className="text-sm" />
                  </span>
                  {contact.text}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white text-base font-semibold uppercase mb-5 pb-2.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[50px] after:h-0.5 after:bg-[var(--primary)]">
              Trực tiếp bóng đá
            </h3>
            <ul className="space-y-3">
              {relatedPlatforms.map((platform, index) => (
                <li key={index}>
                  <a
                    href={platform.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 text-sm hover:text-[var(--primary)] hover:translate-x-1.5 transition-all flex items-center before:content-['\203A'] before:mr-2 before:text-[var(--primary)] before:text-lg before:leading-none"
                  >
                    {platform.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row md:justify-between md:items-center gap-4 text-center md:text-left">
          <div className="text-gray-400 text-sm">
            © 2024 {BRAND.COPYRIGHT}. All rights reserved.
          </div>
          <div className="flex items-center justify-center gap-4 text-sm">
            <Link href="/chinh-sach-bao-mat" className="text-gray-400 hover:text-[var(--primary)] transition-colors">
              Chính sách bảo mật
            </Link>
            <span className="text-gray-600">|</span>
            <Link href="/dieu-khoan-su-dung" className="text-gray-400 hover:text-[var(--primary)] transition-colors">
              Điều khoản sử dụng
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
} 